import { Button, Callout, Divider, H5, Intent } from '@blueprintjs/core'
import * as React from 'react'
import Client from '../../client'
import { Note } from '../../types/api'
import Spinner from '../Spinner'
import Editor from './Editor'
import NoteRenderer from './Note'

export interface NotesProps {
  resource: string
  resourceId: number
  allowNewOrEditNotes?: boolean
  sectionLabel?: string | JSX.Element
  placeholderText?: string
  onUpdateNotes?: (notes: Note[]) => void
  updatedNotes?: boolean
}

interface NotesState {
  loading: boolean
  error: any
  notes: Note[]
  noteBody: string
}

class NotesRenderer extends React.Component<NotesProps, NotesState> {
  static defaultProps = { allowNewOrEditNotes: true }

  state = {
    loading: false,
    error: null,
    noteBody: '',
    notes: []
  }

  componentDidMount() {
    this.fetchNotes()
  }

  componentDidUpdate(prevProps: Readonly<NotesProps>) {
    if (
      prevProps.resourceId !== this.props.resourceId ||
      (prevProps.updatedNotes !== this.props.updatedNotes && this.props.updatedNotes)
    ) {
      this.fetchNotes()
    }
  }

  maybeUpdateNotes() {
    if (this.props.onUpdateNotes) this.props.onUpdateNotes(this.state.notes)
  }

  fetchNotes = async () => {
    this.setState({ loading: true, error: null })
    const { resource, resourceId } = this.props
    try {
      const res = await Client.getNotes(resource, resourceId)
      this.setState(
        {
          loading: false,
          notes: res.data.notes,
          noteBody: ''
        },
        this.maybeUpdateNotes
      )
    } catch (e) {
      this.setState({ error: 'There was an error loading notes, please try again', loading: false })
    }
  }

  onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      noteBody: e.target.value
    })
  }

  addNewNote = async () => {
    const { resource, resourceId } = this.props
    const { noteBody } = this.state
    this.setState({ loading: true })
    try {
      const form = {
        resourceType: resource,
        resourceId,
        body: noteBody
      }
      await Client.createNote(form)
    } catch (e) {
      this.setState({ error: 'There was an error adding that note, please try again', loading: false })
    }

    this.fetchNotes()
  }

  render() {
    const { loading, error, notes, noteBody } = this.state

    if (error) {
      return (
        <div style={{ maxWidth: '100%' }}>
          {this.props.sectionLabel && <H5 className="bp3-heading">{this.props.sectionLabel}</H5>}
          <Callout intent={Intent.DANGER} title="Unexpected error">
            <p>{error}</p>
            <Button onClick={this.fetchNotes} intent={Intent.DANGER}>
              Try again
            </Button>
          </Callout>
        </div>
      )
    }
    return (
      <>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {notes.length > 0 && this.props.sectionLabel && <H5 className="screen-only">{this.props.sectionLabel}</H5>}
            {notes.map((note: Note) => {
              return (
                <NoteRenderer
                  refresh={this.fetchNotes}
                  key={note.id}
                  note={note}
                  allowEdit={this.props.allowNewOrEditNotes}
                />
              )
            })}
            {notes.length > 0 && this.props.allowNewOrEditNotes && <Divider style={{ margin: '30px 0 18px 0' }} />}
            {this.props.allowNewOrEditNotes && (
              <>
                <H5>Add note</H5>
                <Editor
                  onChange={this.onNoteChange}
                  value={noteBody}
                  onSubmit={this.addNewNote}
                  placeholderText={this.props.placeholderText}
                />
              </>
            )}
          </>
        )}
      </>
    )
  }
}

export default NotesRenderer
